'use client';

import { c as _c } from "react/compiler-runtime";
import React, { createContext, ReactNode, useContext, useMemo } from 'react';
import { SubscriptionPlanContext, SubscriptionPlansContext, ConsumerSubscriptionPlan, ConsumerSubscriptionValuePlan, ConsumerSubscriptionGrowWithGoogle, ConsumerSubscriptionGwGPlusPersonalPlan, SubscriptionPlanProductType, useCourseSubscriptionPlan, useConsumerSubscriptionPlan } from '@udemy-v2/subscription-graphql';
import { getActiveConsumerSubscription, useCommonData, CommonDataSubscription } from '@udemy-v2/common-data';
import { useFeatures } from '@udemy-v2/react-experimentation';
export type ClpPurchaseBoxSubscriptionPlan = ConsumerSubscriptionPlan | ConsumerSubscriptionValuePlan | ConsumerSubscriptionGwGPlusPersonalPlan;
export type ClpSubscriptionPlan = ClpPurchaseBoxSubscriptionPlan | ConsumerSubscriptionGrowWithGoogle;

/**
 * Context to manage the increasingly complex subscription plan checks in the CLP
 * This context is used to abstract subscription related context from the CLPPurchaseContextProvider
 * and also enable us to create custom hooks to access specific subscription-related statuses.
 */
export interface ClpSubscriptionContext {
  subscriptionPlan?: SubscriptionPlanContext<ClpSubscriptionPlan>;
  unwrappedConsumerSubscriptionPlan?: ClpPurchaseBoxSubscriptionPlan;
  unwrappedPersonalPlan?: ConsumerSubscriptionPlan;
  unwrappedValuePlan?: ConsumerSubscriptionValuePlan;
  subscribedConsumerSubscriptionPlan?: CommonDataSubscription;
  isPersonalPlanSubscriber: boolean;
  isCourseIncludedInUserSubscription: boolean;
  isCourseInAvailablePlans: boolean;
  isCourseInPersonalPlanCatalog: boolean;
  isPersonalPlanLoading: boolean;
  isSubscriptionPlanLoading: boolean;
  isCourseInValuePlanCatalog: boolean;
  isCourseInAllPlans: boolean;
  isCourseInSelectPlansOnly: boolean;
  isValuePlanSubscriber: boolean;
  subscribedProductType?: SubscriptionPlanProductType;
}

// Exported so story files can provide mock values via ReactClpSubscriptionContext.Provider
export const ReactClpSubscriptionContext = createContext<ClpSubscriptionContext | undefined>(undefined);
export interface ClpSubscriptionContextProviderProps {
  children: ReactNode;
  courseId: string;
}

// TODO: Move this into a service. At the moment the concept of 'tiers' or 'upgrade paths' is not modeled anywhere except here,
// which is hugely unmaintainable.
const PURCHASEABLE_PLAN_TYPES_ORDERED_BY_TIER_ASCENDING: SubscriptionPlanProductType[] = [SubscriptionPlanProductType.ConsumersubscriptionValuePlan, SubscriptionPlanProductType.Consumersubscription, SubscriptionPlanProductType.ConsumersubscriptionPlusGrowWithGoogle];

// A helper function meant to consume an entire SubscriptionPlansContext, and return the lowest tier plan
// that can be purchased via the CLP.
const getLowestTierPurchaseablePlan = (plans: SubscriptionPlansContext, subscriptionTiersEnabled: boolean = false): SubscriptionPlanContext<ClpPurchaseBoxSubscriptionPlan> | undefined => {
  let planToReturn = undefined;

  // Iterate through the tiers backwards, so lower tiers override higher tiers.
  for (const planType of PURCHASEABLE_PLAN_TYPES_ORDERED_BY_TIER_ASCENDING.toReversed()) {
    if (!subscriptionTiersEnabled && planType === SubscriptionPlanProductType.ConsumersubscriptionValuePlan) {
      continue;
    }
    const plan = plans[planType];
    if (plan !== undefined) {
      planToReturn = plan as ClpPurchaseBoxSubscriptionPlan;
    }
  }
  return planToReturn;
};

/** Returns true if a plan context value represents an available (resolved, non-loading) plan. */
const isPlanAvailable = (plan: SubscriptionPlanContext<ClpSubscriptionPlan> | undefined): boolean => plan !== undefined && 'id' in plan && !('isLoading' in plan && plan.isLoading);

/** Returns true if a plan context value is still in its loading state */
const isPlanLoading = (plan: SubscriptionPlanContext<ClpSubscriptionPlan> | undefined): boolean => !!(plan && 'isLoading' in plan && plan.isLoading);

/**
 * Provider that uses the course-specific subscription context to centralize logic that determines
 * subscription plan availability, user subscription status, and course inclusion in subscription plans. This provider computes subscription-related
 * statuses once and memoizes them, providing a single source of truth for subscription-related statuses in the CLP.
 * This enables us to more easily test a single block of business logic and avoid re-computing values on each re-render.
 */
export const ClpSubscriptionContextProvider: React.FC<ClpSubscriptionContextProviderProps> = t0 => {
  const $ = _c(54);
  const {
    children,
    courseId
  } = t0;
  const commonData = useCommonData();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["value_planstarter_plan"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    featureVariantAssignments
  } = useFeatures(t1);
  let t2;
  if ($[1] !== featureVariantAssignments) {
    t2 = featureVariantAssignments.get("value_planstarter_plan")?.configuration?.enableValuePlan ?? false;
    $[1] = featureVariantAssignments;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const subscriptionTiersEnabled = t2 as boolean;
  let t3;
  if ($[3] !== commonData) {
    t3 = getActiveConsumerSubscription(commonData, true);
    $[3] = commonData;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const subscribedConsumerSubscriptionPlan = t3;
  const subscribedProductType = subscribedConsumerSubscriptionPlan?.subscribedPlan?.productType as SubscriptionPlanProductType | undefined;
  const consumerSubscriptionPlan = useCourseSubscriptionPlan(courseId, SubscriptionPlanProductType.Consumersubscription) as SubscriptionPlanContext<ConsumerSubscriptionPlan> | undefined;
  const valueSubscriptionPlan = useCourseSubscriptionPlan(courseId, SubscriptionPlanProductType.ConsumersubscriptionValuePlan) as SubscriptionPlanContext<ConsumerSubscriptionValuePlan> | undefined;
  const gwgPlusPersonalPlan = useCourseSubscriptionPlan(courseId, SubscriptionPlanProductType.ConsumersubscriptionPlusGrowWithGoogle) as SubscriptionPlanContext<ConsumerSubscriptionGwGPlusPersonalPlan> | undefined;
  const gwgSubscriptionPlan = useCourseSubscriptionPlan(courseId, SubscriptionPlanProductType.GrowWithGoogle) as SubscriptionPlanContext<ConsumerSubscriptionGrowWithGoogle> | undefined;
  const personalPlan = useConsumerSubscriptionPlan();
  let isCourseInPersonalPlanCatalog;
  let isCourseInValuePlanCatalog;
  let plansByProductType;
  let subscriptionPlan;
  let t4;
  let unwrappedConsumerSubscriptionPlan;
  let unwrappedPersonalPlan;
  let unwrappedValuePlan;
  if ($[5] !== consumerSubscriptionPlan || $[6] !== gwgPlusPersonalPlan || $[7] !== gwgSubscriptionPlan || $[8] !== personalPlan || $[9] !== subscribedProductType || $[10] !== subscriptionTiersEnabled || $[11] !== valueSubscriptionPlan) {
    plansByProductType = {
      [SubscriptionPlanProductType.Consumersubscription]: consumerSubscriptionPlan,
      [SubscriptionPlanProductType.ConsumersubscriptionValuePlan]: valueSubscriptionPlan,
      [SubscriptionPlanProductType.ConsumersubscriptionPlusGrowWithGoogle]: gwgPlusPersonalPlan,
      [SubscriptionPlanProductType.GrowWithGoogle]: gwgSubscriptionPlan
    };
    const allPlans = Object.values(plansByProductType);
    let t5;
    if ($[20] !== consumerSubscriptionPlan || $[21] !== gwgPlusPersonalPlan || $[22] !== subscriptionTiersEnabled || $[23] !== valueSubscriptionPlan) {
      const plans = {
        [SubscriptionPlanProductType.ConsumersubscriptionValuePlan]: valueSubscriptionPlan,
        [SubscriptionPlanProductType.Consumersubscription]: consumerSubscriptionPlan,
        [SubscriptionPlanProductType.ConsumersubscriptionPlusGrowWithGoogle]: gwgPlusPersonalPlan
      } as SubscriptionPlansContext;
      t5 = getLowestTierPurchaseablePlan(plans, subscriptionTiersEnabled);
      $[20] = consumerSubscriptionPlan;
      $[21] = gwgPlusPersonalPlan;
      $[22] = subscriptionTiersEnabled;
      $[23] = valueSubscriptionPlan;
      $[24] = t5;
    } else {
      t5 = $[24];
    }
    const purchaseBoxSubscriptionPlan = t5;
    subscriptionPlan = purchaseBoxSubscriptionPlan ?? gwgSubscriptionPlan;
    unwrappedConsumerSubscriptionPlan = purchaseBoxSubscriptionPlan && "id" in purchaseBoxSubscriptionPlan ? purchaseBoxSubscriptionPlan : undefined;
    unwrappedPersonalPlan = personalPlan && "id" in personalPlan ? personalPlan as ConsumerSubscriptionPlan : undefined;
    unwrappedValuePlan = valueSubscriptionPlan && "id" in valueSubscriptionPlan ? valueSubscriptionPlan : undefined;
    const isGwGStandaloneSubscriber = subscribedProductType === SubscriptionPlanProductType.GrowWithGoogle;
    const gwgPlanCoversThisCourse = !!(gwgSubscriptionPlan && "isSubscriber" in gwgSubscriptionPlan && gwgSubscriptionPlan.isSubscriber);
    let t6;
    if ($[25] !== consumerSubscriptionPlan || $[26] !== gwgPlanCoversThisCourse || $[27] !== gwgPlusPersonalPlan || $[28] !== isGwGStandaloneSubscriber) {
      t6 = isPlanAvailable(consumerSubscriptionPlan) || isGwGStandaloneSubscriber && isPlanAvailable(gwgPlusPersonalPlan) && !gwgPlanCoversThisCourse;
      $[25] = consumerSubscriptionPlan;
      $[26] = gwgPlanCoversThisCourse;
      $[27] = gwgPlusPersonalPlan;
      $[28] = isGwGStandaloneSubscriber;
      $[29] = t6;
    } else {
      t6 = $[29];
    }
    isCourseInPersonalPlanCatalog = t6;
    let t7;
    if ($[30] !== valueSubscriptionPlan) {
      t7 = isPlanAvailable(valueSubscriptionPlan);
      $[30] = valueSubscriptionPlan;
      $[31] = t7;
    } else {
      t7 = $[31];
    }
    isCourseInValuePlanCatalog = t7;
    t4 = allPlans.some(isPlanAvailable);
    $[5] = consumerSubscriptionPlan;
    $[6] = gwgPlusPersonalPlan;
    $[7] = gwgSubscriptionPlan;
    $[8] = personalPlan;
    $[9] = subscribedProductType;
    $[10] = subscriptionTiersEnabled;
    $[11] = valueSubscriptionPlan;
    $[12] = isCourseInPersonalPlanCatalog;
    $[13] = isCourseInValuePlanCatalog;
    $[14] = plansByProductType;
    $[15] = subscriptionPlan;
    $[16] = t4;
    $[17] = unwrappedConsumerSubscriptionPlan;
    $[18] = unwrappedPersonalPlan;
    $[19] = unwrappedValuePlan;
  } else {
    isCourseInPersonalPlanCatalog = $[12];
    isCourseInValuePlanCatalog = $[13];
    plansByProductType = $[14];
    subscriptionPlan = $[15];
    t4 = $[16];
    unwrappedConsumerSubscriptionPlan = $[17];
    unwrappedPersonalPlan = $[18];
    unwrappedValuePlan = $[19];
  }
  const isCourseInAvailablePlans = t4;
  const isCourseInAllPlans = isCourseInValuePlanCatalog && isCourseInPersonalPlanCatalog;
  const isCourseInSelectPlansOnly = isCourseInPersonalPlanCatalog && !isCourseInValuePlanCatalog;
  const isSubscriptionPlanLoading = !!(subscriptionPlan && "isLoading" in subscriptionPlan && subscriptionPlan.isLoading);
  let t5;
  if ($[32] !== consumerSubscriptionPlan) {
    t5 = isPlanLoading(consumerSubscriptionPlan);
    $[32] = consumerSubscriptionPlan;
    $[33] = t5;
  } else {
    t5 = $[33];
  }
  const isPersonalPlanLoading = t5;
  const isPersonalPlanSubscriber = !!subscribedConsumerSubscriptionPlan;
  const isValuePlanSubscriber = subscribedProductType === SubscriptionPlanProductType.ConsumersubscriptionValuePlan;
  const subscriptionPlanForProductType = subscribedProductType ? plansByProductType[subscribedProductType] : undefined;
  const isCourseIncludedInUserSubscription = !!(subscribedProductType && subscriptionPlanForProductType && "isSubscriber" in subscriptionPlanForProductType && subscriptionPlanForProductType.isSubscriber);
  const t6 = subscribedConsumerSubscriptionPlan ?? undefined;
  let t7;
  if ($[34] !== isCourseInAllPlans || $[35] !== isCourseInAvailablePlans || $[36] !== isCourseInPersonalPlanCatalog || $[37] !== isCourseInSelectPlansOnly || $[38] !== isCourseInValuePlanCatalog || $[39] !== isCourseIncludedInUserSubscription || $[40] !== isPersonalPlanLoading || $[41] !== isPersonalPlanSubscriber || $[42] !== isSubscriptionPlanLoading || $[43] !== isValuePlanSubscriber || $[44] !== subscribedProductType || $[45] !== subscriptionPlan || $[46] !== t6 || $[47] !== unwrappedConsumerSubscriptionPlan || $[48] !== unwrappedPersonalPlan || $[49] !== unwrappedValuePlan) {
    t7 = {
      subscriptionPlan,
      unwrappedConsumerSubscriptionPlan,
      unwrappedPersonalPlan,
      unwrappedValuePlan,
      subscribedConsumerSubscriptionPlan: t6,
      isPersonalPlanSubscriber,
      isCourseIncludedInUserSubscription,
      isCourseInAvailablePlans,
      isCourseInPersonalPlanCatalog,
      isPersonalPlanLoading,
      isSubscriptionPlanLoading,
      isCourseInValuePlanCatalog,
      isCourseInAllPlans,
      isCourseInSelectPlansOnly,
      isValuePlanSubscriber,
      subscribedProductType
    };
    $[34] = isCourseInAllPlans;
    $[35] = isCourseInAvailablePlans;
    $[36] = isCourseInPersonalPlanCatalog;
    $[37] = isCourseInSelectPlansOnly;
    $[38] = isCourseInValuePlanCatalog;
    $[39] = isCourseIncludedInUserSubscription;
    $[40] = isPersonalPlanLoading;
    $[41] = isPersonalPlanSubscriber;
    $[42] = isSubscriptionPlanLoading;
    $[43] = isValuePlanSubscriber;
    $[44] = subscribedProductType;
    $[45] = subscriptionPlan;
    $[46] = t6;
    $[47] = unwrappedConsumerSubscriptionPlan;
    $[48] = unwrappedPersonalPlan;
    $[49] = unwrappedValuePlan;
    $[50] = t7;
  } else {
    t7 = $[50];
  }
  const value = t7;
  let t8;
  if ($[51] !== children || $[52] !== value) {
    t8 = <ReactClpSubscriptionContext.Provider value={value}>{children}</ReactClpSubscriptionContext.Provider>;
    $[51] = children;
    $[52] = value;
    $[53] = t8;
  } else {
    t8 = $[53];
  }
  return t8;
};

/**
 * Hook to access the CLP subscription context
 * @returns ClpSubscriptionContext
 * @throws Error if used outside of ClpSubscriptionContextProvider
 */
export function useClpSubscriptionContext() {
  const context = useContext(ReactClpSubscriptionContext);
  if (context === undefined) {
    throw new Error("useClpSubscriptionContext must be used within a ClpSubscriptionContextProvider");
  }
  return context;
}